import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas, qaSchema } from "@/lib/seo";

const UPDATED = "2026-09-10";
const ANSWER =
  "Eine Agentur passt, wenn Design, Entwicklung, Inhalte und Werbung parallel laufen und ein Team koordiniert werden muss. Ein Freelancer passt, wenn das Vorhaben klar umrissen ist: Sie sprechen direkt mit der Person, die im Shop arbeitet, und sparen Abstimmungsrunden. Entscheidend ist nicht der Stundensatz, sondern wie viele Stunden tatsächlich im Shop landen.";

const SITE = "https://shopify-freelancer.net";
const PATH = "/fragen/shopify-agentur-oder-freelancer";
const TITLE = "Shopify Agentur oder Freelancer? Kurze Entscheidungshilfe";
const DESCRIPTION =
  "Wann ein Team sinnvoll ist und wann ein Shopify Freelancer der kürzere Weg: Unterschiede, Kosten, Risiken und Fragen, die Angebote vergleichbar machen.";

const faqs = [
  {
    q: "Agentur oder Freelancer, was ist besser?",
    a: "Es hängt vom Umfang ab. Laufen Design, Entwicklung, Inhalte und Werbung parallel, hilft ein Team. Geht es um Shopaufbau, Theme-Entwicklung oder Conversion, arbeiten Sie mit einem Freelancer direkt mit der Person, die umsetzt.",
  },
  {
    q: "Ist ein Freelancer günstiger?",
    a: "Meist ja, weil Projektleitung und Abstimmungsrunden wegfallen. Der Stundensatz allein sagt wenig, entscheidend ist, wie viele Stunden tatsächlich im Shop landen.",
  },
  {
    q: "Was ist das Risiko bei einem Freelancer?",
    a: "Verfügbarkeit. Eine Person kann krank oder ausgebucht sein. Wichtig ist deshalb, dass alles in Ihrem eigenen Shopify-Konto liegt und ein anderer Entwickler jederzeit übernehmen kann.",
  },
  {
    q: "Woran erkenne ich ein belastbares Angebot?",
    a: "Es nennt den konkreten Umfang, wer die Arbeit ausführt, wie abgerechnet wird, welcher Zeitrahmen gilt und was bei Änderungen passiert. Fehlen diese Punkte, lässt sich das Angebot nicht vergleichen.",
  },
  {
    q: "Was kostet ein Shopify Freelancer?",
    a: "Bei mir 110 € netto pro Stunde für Anpassungen und Entwicklung, Festpreis nach Umfang für den Shopaufbau, Conversion-Optimierung ab 3.990 € netto. Keine Mindestlaufzeit.",
  },
];

export const Route = createFileRoute("/fragen/shopify-agentur-oder-freelancer")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: [
      ...articleSchemas({
        title: TITLE,
        description: DESCRIPTION,
        path: PATH,
        faqs,
        section: "Shopify Zusammenarbeit",
        updated: UPDATED,
        breadcrumb: { name: "Fragen", path: "/fragen" },
      }),
      qaSchema({
        question: "Shopify Agentur oder Freelancer?",
        answer: ANSWER,
        path: PATH,
        updated: UPDATED,
      }),
    ],
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Frage · Zusammenarbeit"
      title="Shopify Agentur oder Freelancer?"
      intro="Kurz gesagt: Ein Team lohnt sich, wenn viele Aufgaben gleichzeitig laufen. Ein Freelancer lohnt sich, wenn das Vorhaben klar umrissen ist und Sie mit der Person sprechen wollen, die tatsächlich im Shop arbeitet."
      source="frage-agentur-oder-freelancer"
      answer={ANSWER}
      updated={UPDATED}
      breadcrumb={{ to: "/fragen", label: "Fragen" }}
      sections={[
        {
          h: "Der Unterschied in einem Satz",
          p: [
            "Ein Team verteilt Ihr Projekt auf Rollen und braucht Abstimmung. Ein Freelancer bündelt Beratung und Umsetzung in einer Person und spart genau diese Abstimmung.",
          ],
        },
        {
          h: "Wann ein Team sinnvoll ist",
          p: [],
          list: [
            "Mehrere Länder, Sprachen oder Shops gleichzeitig",
            "Design, Inhalte, Entwicklung und Werbung laufen parallel",
            "Feste Reaktionszeiten und Vertretung sind vertraglich nötig",
          ],
        },
        {
          h: "Wann ein Freelancer der kürzere Weg ist",
          p: [],
          list: [
            "Ein neuer Shop soll sauber aufgebaut und übergeben werden",
            "Eine konkrete Anpassung, Funktion oder ein Fehler steht an",
            "Der Shop läuft, soll aber pro Besucher mehr verkaufen",
            "Kleinere Aufgaben sollen nicht an einem Mindestbudget scheitern",
          ],
        },
        {
          h: "Fragen, die Angebote vergleichbar machen",
          p: [],
          list: [
            "Wer arbeitet tatsächlich im Shop?",
            "Was genau ist im Preis enthalten, was nicht?",
            "Wird in einer Theme-Kopie oder im laufenden Shop gearbeitet?",
            "Wovon hängt der Termin ab?",
            "Was passiert nach dem Start mit Fehlern und Anpassungen?",
          ],
        },
        {
          h: "Worauf es am Ende ankommt",
          p: [
            "Alles muss in Ihrem eigenen Shopify-Konto liegen, ohne Abhängigkeit von einem Dienstleister. Dann ist die Entscheidung zwischen Team und Einzelperson keine Einbahnstraße.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/shopify-freelancer", label: "Shopify Freelancer" },
        { to: "/shopify-agentur", label: "Agentur und Freelancer im Vergleich" },
        { to: "/ratgeber/shopify-agentur-kosten", label: "Kosten im Detail" },
      ]}
    />
  );
}
